import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2, ChevronRight, Download } from "lucide-react";
import { api, imageUrl, videoUrl, fileUrl } from "@/lib/api";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { useJsonLd } from "@/hooks/useJsonLd";
import { ShareBar } from "@/components/ShareBar";
import { JURISDICTIONS, ATLAS_BASE_PATH } from "@/data/jurisdictions";

// Insights jurisdiction names -> atlas entries (exact, case-insensitive).
const ATLAS_BY_NAME = {};
JURISDICTIONS.forEach((j) => {
  ATLAS_BY_NAME[j.name.toLowerCase()] = j;
});

const MarkdownLink = ({ href, children, ...props }) => {
  const isExternal = /^https?:\/\//i.test(href || "");
  return isExternal ? (
    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  ) : (
    <a href={href} {...props}>
      {children}
    </a>
  );
};

const MARKDOWN_COMPONENTS = { a: MarkdownLink };

export default function ArticleDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error

  useDocumentMeta({
    title: article ? article.title : "Insights",
    description: article?.summary || undefined,
    path: `/insights/${slug}`,
    image: article?.cover_image_id ? imageUrl(article.cover_image_id) : undefined,
    type: article && article.content_type !== "video" ? "article" : undefined,
  });

  const readingMinutes = article?.body
    ? Math.max(1, Math.round(article.body.trim().split(/\s+/).length / 200))
    : 0;

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  useJsonLd(
    "ld-article",
    article && article.content_type !== "external"
      ? article.content_type === "video"
        ? {
            "@context": "https://schema.org",
            "@type": "VideoObject",
            name: article.title,
            description: article.summary || article.title,
            uploadDate: article.published_at || article.created_at,
            thumbnailUrl: article.cover_image_id ? `${origin}/api/insights/image/${article.cover_image_id}` : undefined,
            contentUrl: article.video_file_id ? `${origin}/api/insights/video/${article.video_file_id}` : undefined,
          }
        : {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.summary || undefined,
            datePublished: article.published_at || article.created_at,
            dateModified: article.updated_at || undefined,
            image: article.cover_image_id ? `${origin}/api/insights/image/${article.cover_image_id}` : undefined,
            author: { "@type": "Organization", name: "AMADI Legal Consultancy" },
            publisher: { "@type": "Organization", name: "AMADI Legal Consultancy" },
            mainEntityOfPage: `${origin}/insights/${slug}`,
          }
      : null
  );

  useEffect(() => {
    let active = true;
    api
      .get(`/api/insights/${slug}`, { params: { t: Date.now() }, withCredentials: false })
      .then(({ data }) => {
        if (!active) return;
        // External articles have no on-site page — send visitors to the link.
        if (data.content_type === "external" && data.external_url) {
          window.location.href = data.external_url;
          return;
        }
        setArticle(data);
        setStatus("ready");
      })
      .catch(() => active && setStatus("error"));
    return () => {
      active = false;
    };
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-charcoal/40 animate-spin" />
      </div>
    );
  }

  if (status === "error" || !article) {
    return (
      <div data-testid="article-not-found" className="min-h-screen bg-canvas flex flex-col items-center justify-center px-6 text-center">
        <h1 className="font-serif text-3xl text-charcoal">Article not found.</h1>
        <button
          onClick={() => navigate("/insights")}
          className="mt-6 font-sans text-xs tracking-[0.14em] uppercase font-bold px-7 py-4 bg-forest text-canvas hover:bg-charcoal transition-colors"
        >
          Back to Insights
        </button>
      </div>
    );
  }

  return (
    <div data-testid="article-detail-page">
      {/* Hero */}
      <section className="relative bg-charcoal text-canvas overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-charcoal via-charcoal to-forest/40 pointer-events-none" />
        <div className="relative max-w-[820px] mx-auto px-6 lg:px-8 pt-36 pb-16 md:pt-44 md:pb-20">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2.5 font-sans text-[11px] tracking-[0.14em] uppercase">
            <Link to="/" className="text-canvas/50 hover:text-gold transition-colors">Home</Link>
            <ChevronRight className="w-3 h-3 text-canvas/30" strokeWidth={1.5} />
            <Link to="/insights" className="text-canvas/50 hover:text-gold transition-colors">Insights</Link>
          </nav>
          <span className="mt-10 block font-sans text-[11px] tracking-[0.28em] uppercase text-gold">
            {article.tag}
            {article.display_date && (
              <span className="text-canvas/50"> &middot; {article.display_date}</span>
            )}
            {readingMinutes > 0 && (
              <span data-testid="article-reading-time" className="text-canvas/50"> &middot; {readingMinutes} min read</span>
            )}
          </span>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.1 }}
            data-testid="article-title"
            className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1]"
          >
            {article.title}
          </motion.h1>
          {article.summary && (
            <p className="mt-6 font-serif text-lg md:text-xl text-canvas/70 leading-relaxed">
              {article.summary}
            </p>
          )}
          {article.jurisdictions && article.jurisdictions.length > 0 && (
            <div data-testid="article-jurisdictions" className="mt-7">
              <span className="block font-sans text-[10px] tracking-[0.2em] uppercase text-canvas/40 mb-3">
                Jurisdictions covered
              </span>
              <div className="flex flex-wrap gap-2">
                {article.jurisdictions.map((j) => {
                  const match = ATLAS_BY_NAME[j.toLowerCase()];
                  const chipClass =
                    "inline-flex items-center gap-1.5 font-sans text-[10px] tracking-[0.16em] uppercase text-gold/90 border border-gold/40 px-2.5 py-1";
                  return match ? (
                    <Link
                      key={j}
                      to={`${ATLAS_BASE_PATH}/${match.slug}`}
                      data-testid={`article-jurisdiction-link-${match.slug}`}
                      title={`View ${match.name} on the atlas`}
                      aria-label={`View ${match.name} on the atlas`}
                      className={`${chipClass} transition-colors duration-200 hover:text-gold hover:border-gold`}
                    >
                      {j}
                      <span aria-hidden="true" className="text-[11px] leading-none">→</span>
                    </Link>
                  ) : (
                    <span key={j} className={chipClass}>
                      {j}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Cover */}
      {article.cover_image_id && (
        <div className="bg-canvas">
          <div className="max-w-[820px] mx-auto px-6 lg:px-8 -mt-8 md:-mt-10">
            <div className="aspect-[16/9] overflow-hidden border border-charcoal/10">
              <img src={imageUrl(article.cover_image_id)} alt={article.title} loading="lazy" decoding="async" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      )}

      {/* Video */}
      {article.content_type === "video" && article.video_file_id && (
        <section className="bg-canvas pt-16 md:pt-24">
          <div className="max-w-[900px] mx-auto px-6 lg:px-8">
            <video
              data-testid="article-video"
              key={article.video_file_id}
              src={videoUrl(article.video_file_id)}
              controls
              playsInline
              poster={article.cover_image_id ? imageUrl(article.cover_image_id) : undefined}
              className="w-full aspect-video bg-charcoal border border-charcoal/10"
            />
          </div>
        </section>
      )}

      {/* Resource download */}
      {article.content_type === "resource" && article.resource_file_id && (
        <section className="bg-canvas pt-16 md:pt-24">
          <div className="max-w-[720px] mx-auto px-6 lg:px-8">
            <a
              data-testid="article-download"
              href={fileUrl(article.resource_file_id)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 font-sans text-xs tracking-[0.14em] uppercase font-bold px-8 py-4 bg-forest text-canvas border border-forest hover:bg-charcoal hover:border-charcoal transition-colors duration-300"
            >
              <Download className="w-4 h-4" strokeWidth={1.6} />
              Download {article.resource_filename || "file"}
            </a>
          </div>
        </section>
      )}

      {/* Body (markdown) — for on-site articles, or optional notes on video/resource */}
      {article.body && article.body.trim() && (
        <section className="bg-canvas py-16 md:py-24">
          <div
            data-testid="article-body"
            className="article-prose max-w-[720px] mx-auto px-6 lg:px-8"
          >
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={MARKDOWN_COMPONENTS}
            >
              {article.body}
            </ReactMarkdown>
            <div className="mt-12 pt-8 border-t border-charcoal/12">
              <ShareBar title={article.title} />
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
